// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { mergeClasses, Toast, ToastTitle, useToastController } from '@fluentui/react-components';
import { Add20Regular, Link20Regular } from '@fluentui/react-icons';
import { ErrorCode } from '@microsoft/vscodeedu-api';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { SignInContext } from '../app';
import { IntlProps, NotSignedInState, TrackContext, TrackedButton } from '../components/common';
import CreateActivityDialog from '../components/curriculum-list/create-activity-dialog';
import CurriculumList from '../components/curriculum-list/curriculum-list';
import DeleteActivityDialog from '../components/curriculum-list/delete-activity-dialog';
import LinkGithubDialog from '../components/curriculum-list/link-github-dialog';
import LinkRepoDialog from '../components/curriculum-list/link-repo-dialog';
import UnpublishActivityDialog from '../components/curriculum-list/unpublish-activity-dialog';
import PageContainer from '../components/page-container';
import { getIsSignedIn, UserContext } from '../data';
import { getIsLoadingCurricula, useCurricula } from '../data/curriculum-context';
import { useAppStyles } from '../styles';

// Authoring page.
export default injectIntl(({ intl }: IntlProps) => {
    const appStyles = useAppStyles();
    const [userStore, userDispatch] = useContext(UserContext);
    const isSignedIn = getIsSignedIn(userStore);
    const [store, dispatch] = useCurricula();
    const isLoading = getIsLoadingCurricula(store);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [linkRepoDialogOpen, setLinkRepoDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<string | undefined>(undefined);
    const [unpublishTarget, setUnpublishTarget] = useState<string | undefined>(undefined);

    const [githubRedirectUrl, setGithubRedirectUrl] = React.useState('');
    useEffect(() => {
        if (store.lastError?.code === ErrorCode.MissingGitHubAccess) {
            setGithubRedirectUrl('/authoring');
        }
    }, [store.lastError?.code]);

    const onCreateNewActivity = useCallback(() => {
        if (userStore.isGithubLinked !== true) {
            setGithubRedirectUrl('/authoring/new');
        } else {
            setCreateDialogOpen(true);
        }
    }, [userStore.isGithubLinked]);

    const onLinkExisting = useCallback(() => {
        if (userStore.isGithubLinked !== true) {
            setGithubRedirectUrl('/authoring/link');
        } else {
            setLinkRepoDialogOpen(true);
        }
    }, [userStore.isGithubLinked]);

    const navigate = useNavigate();
    const { ensureSignedIn } = useContext(SignInContext);

    const { state: routeState } = useLocation();
    const { dispatchToast } = useToastController('app-toaster');
    useEffect(() => {
        if (routeState === 'account-linked') {
            dispatchToast(
                <Toast appearance="inverted">
                    <ToastTitle>
                        <FormattedMessage
                            defaultMessage="Successfully linked GitHub account."
                            description="Message upon linking GitHub account."
                        />
                    </ToastTitle>
                </Toast>,
                { intent: 'success' }
            );
        }
    }, [dispatchToast, routeState]);

    const newActivityRoute = useMatch('/authoring/new');
    useEffect(() => {
        if (newActivityRoute) {
            if (!isSignedIn) {
                ensureSignedIn('/authoring/new');
            } else if (!isLoading) {
                navigate('/authoring', { replace: true });
                setCreateDialogOpen(true);
            }
        }
    }, [ensureSignedIn, isLoading, isSignedIn, navigate, newActivityRoute]);

    const linkExistingRoute = useMatch('/authoring/link');
    useEffect(() => {
        if (linkExistingRoute) {
            if (!isSignedIn) {
                ensureSignedIn('/authoring/link');
            } else if (!isLoading) {
                navigate('/authoring', { replace: true });
                setLinkRepoDialogOpen(true);
            }
        }
    }, [ensureSignedIn, isLoading, isSignedIn, linkExistingRoute, navigate]);

    const pageTitle = intl.formatMessage({
        description: 'Curriculum authoring page title.',
        defaultMessage: 'Authoring',
    });

    return (
        <TrackContext.Provider value={{ container: 'authoring' }}>
            <PageContainer className={appStyles.pageContainer}>
                <div className={mergeClasses(appStyles.pageContent, 'content-width')}>
                    {!isSignedIn && <NotSignedInState />}
                    {isSignedIn && !isLoading && (
                        <div className={mergeClasses(appStyles.section)}>
                            <div className={appStyles.flexRowCentered}>
                                <h1 className={appStyles.sectionTitle}>{pageTitle}</h1>
                                <div className={appStyles.flexGrow1} />
                                {store.items.length > 0 && (
                                    <>
                                        <div
                                            className={mergeClasses(appStyles.mobileHidden, appStyles.flexRowSmallGap)}
                                        >
                                            <TrackedButton
                                                trackAction="link-existing"
                                                size="large"
                                                onClick={onLinkExisting}
                                            >
                                                <FormattedMessage
                                                    description="Link existing repo button title"
                                                    defaultMessage="Link existing repo"
                                                />
                                            </TrackedButton>
                                            <TrackedButton
                                                trackAction="new-activity"
                                                className={appStyles.buttonPrimary}
                                                appearance="primary"
                                                size="large"
                                                onClick={onCreateNewActivity}
                                            >
                                                <FormattedMessage
                                                    description="Create new activity button title"
                                                    defaultMessage="Create new"
                                                />
                                            </TrackedButton>
                                        </div>
                                        <div className={mergeClasses(appStyles.mobileOnly, appStyles.flexRowSmallGap)}>
                                            <TrackedButton
                                                trackAction="link-existing"
                                                size="medium"
                                                shape="circular"
                                                icon={<Link20Regular />}
                                                onClick={onLinkExisting}
                                                title={intl.formatMessage({
                                                    description: 'Tooltip for link existing repo button',
                                                    defaultMessage: 'Link existing repository',
                                                })}
                                            />
                                            <TrackedButton
                                                trackAction="new-activity"
                                                className={appStyles.mobileOnly}
                                                appearance="primary"
                                                size="medium"
                                                shape="circular"
                                                icon={<Add20Regular />}
                                                onClick={onCreateNewActivity}
                                                title={intl.formatMessage({
                                                    description: 'Tooltip for create activity button',
                                                    defaultMessage: 'Create new',
                                                })}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className={mergeClasses(appStyles.sectionContent, appStyles.flexGrow1)}>
                                <CurriculumList
                                    items={store.items}
                                    onCreate={onCreateNewActivity}
                                    onLink={onLinkExisting}
                                    onPublish={(curriculumId) => dispatch({ type: 'Publish', curriculumId })}
                                    onUnpublish={(curriculumId) => setUnpublishTarget(curriculumId)}
                                    onDelete={(curriculumId) => setDeleteTarget(curriculumId)}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <CreateActivityDialog
                    open={createDialogOpen}
                    onOpenChange={(_, { open }) => setCreateDialogOpen(open)}
                    onCreate={(props) => dispatch({ type: 'Create', curriculum: props })}
                />

                <LinkRepoDialog
                    open={linkRepoDialogOpen}
                    onOpenChange={(_, { open }) => setLinkRepoDialogOpen(open)}
                    onLink={(repoName, platform) => dispatch({ type: 'LinkRepo', repoName, platform })}
                />

                <UnpublishActivityDialog
                    open={unpublishTarget !== undefined}
                    onOpenChange={(_, { open }) => setUnpublishTarget(open ? unpublishTarget : undefined)}
                    title={store.items.find((c) => c.curriculumId === unpublishTarget)?.title ?? ''}
                    onUnpublish={() => dispatch({ type: 'Unpublish', curriculumId: unpublishTarget! })}
                />

                <DeleteActivityDialog
                    open={deleteTarget !== undefined}
                    onOpenChange={(_, { open }) => setDeleteTarget(open ? deleteTarget : undefined)}
                    title={store.items.find((c) => c.curriculumId === deleteTarget)?.title ?? ''}
                    repoName={store.items.find((c) => c.curriculumId === deleteTarget)?.repoName}
                    onDelete={() => dispatch({ type: 'Delete', curriculumId: deleteTarget! })}
                />

                <LinkGithubDialog
                    open={githubRedirectUrl !== ''}
                    onOpenChange={() => setGithubRedirectUrl('')}
                    onConfirm={() => userDispatch({ type: 'LinkGithubAccount', redirectUrl: githubRedirectUrl })}
                    onClose={() => dispatch({ type: 'Reload' })}
                />
            </PageContainer>
        </TrackContext.Provider>
    );
});
